import { useLocalStorageState } from 'ahooks';
import { useCallback } from 'react';
import type { TextPromptWithInputItem } from '@/app/types';

export const REFERENCE_DELETE_CONFIRMATION_SHOW_LIMIT = 3;

export const getReferenceDeleteConfirmationCountKey = (taskType: string) =>
  `VIDU_${taskType.toUpperCase()}_CONFIRMATION_COUNT`;

interface UseReferenceDeleteConfirmationOptions {
  confirmationCountKey: string;
  onConfirmDelete: (referenceIdsToDelete: string[], updatedContent: TextPromptWithInputItem) => void;
  onShowConfirm: (referenceIdsToDelete: string[], updatedContent: TextPromptWithInputItem) => boolean;
}

export const useReferenceDeleteConfirmation = ({
  confirmationCountKey,
  onConfirmDelete,
  onShowConfirm,
}: UseReferenceDeleteConfirmationOptions) => {
  const [confirmationCount, setConfirmationCount] = useLocalStorageState<number>(confirmationCountKey, {
    defaultValue: 0,
    listenStorageChange: true,
  });

  const handleBeforeDeleteMention = useCallback(
    (mentionsIdsToDelete: string[], updatedContent: TextPromptWithInputItem) => {
      const referenceIdsToDelete = mentionsIdsToDelete.filter((mentionId) => {
        const mentionExist = updatedContent.find((item) => item.type === 'mention' && item.mention.id === mentionId);
        return !mentionExist;
      });

      if (referenceIdsToDelete.length === 0) {
        return true;
      }

      if ((confirmationCount ?? 0) >= REFERENCE_DELETE_CONFIRMATION_SHOW_LIMIT) {
        onConfirmDelete(referenceIdsToDelete, updatedContent);
        return true;
      }

      const canUpdateContent = onShowConfirm(referenceIdsToDelete, updatedContent);
      if (!canUpdateContent) {
        setConfirmationCount((confirmationCount ?? 0) + 1);
      }

      return canUpdateContent;
    },
    [confirmationCount, onConfirmDelete, onShowConfirm, setConfirmationCount],
  );

  return {
    handleBeforeDeleteMention,
  };
};
